import type { DeviceNavigationItem } from '@features/devices/types';
import type { AntdMenuItem } from '@shared/types/menu';
import { normalizeDeviceHref, normalizeLabelKey } from './menuNormalization';
import { resolveNavigationIcon, navigationLabelFallbackMap } from './menuUtils';

/**
 * Удалить дубликаты из навигации устройств
 */
export function pruneDuplicateDeviceNavigationItems(
  items: DeviceNavigationItem[] | undefined,
  seenTargets: Set<string> = new Set(),
): DeviceNavigationItem[] {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => {
      const normalizedHref = item.href ? normalizeDeviceHref(item.href) : undefined;
      const nextChildren = pruneDuplicateDeviceNavigationItems(item.children, seenTargets);

      if (normalizedHref && seenTargets.has(normalizedHref)) {
        // Дубликат пути — игнорируем пункт, но оставляем уникальные подэлементы
        return nextChildren.length > 0 ? { ...item, children: nextChildren } : null;
      }

      if (normalizedHref) {
        seenTargets.add(normalizedHref);
      }

      if (nextChildren !== item.children) {
        return { ...item, children: nextChildren };
      }

      return item;
    })
    .filter((node): node is DeviceNavigationItem => node !== null);
}

/**
 * Трансформировать навигационные элементы устройств в пункты меню Ant Design
 */
export function transformNavigationItems(
  items: DeviceNavigationItem[] | undefined,
  translate: (key: string, defaultValue?: string) => string,
): AntdMenuItem[] {
  if (!items || items.length === 0) {
    return [];
  }

  return items
    .map((item) => {
      const children = transformNavigationItems(item.children, translate);
      const target = normalizeDeviceHref(item.href);
      if (!target && children.length === 0) {
        return null;
      }

      const key = target ?? `devices::${item.id}`;
      const normalizedLabel = normalizeLabelKey(item.label);
      let translationKey =
        item.label?.includes('.') || item.label?.includes('_')
          ? item.label
          : navigationLabelFallbackMap[normalizedLabel ?? ''] ?? item.label;
      
      // Конвертируем формат "menu.devices_olt" → "menu:devices_olt" для i18next
      if (translationKey && translationKey.includes('.')) {
        translationKey = translationKey.replace('.', ':');
      }
      
      const translatedLabel = translationKey ? translate(translationKey) : undefined;
      const resolvedLabel =
        translationKey && translatedLabel && translatedLabel !== translationKey
          ? translatedLabel
          : item.label ?? translatedLabel ?? translationKey ?? key;
      const menuItem: AntdMenuItem = {
        key,
        label: resolvedLabel,
        icon: resolveNavigationIcon(item.icon),
        children: children.length > 0 ? children : undefined,
      };

      return menuItem;
    })
    .filter(Boolean) as AntdMenuItem[];
}

